import React from "react";

import { formatCurrencyRange, formatNumericCurrency, useGetFarms, useInventoryImpact, useTranslation } from "utils";

import { SingleFieldOutcomes } from "models";

import { Flex, IconLinkButton, Tag, Tooltip, Typography } from "components";

type OutcomeBasedImpactProps = {
  isDividePerHectare: boolean;
  maximumPayoutPerHa?: number | null;
  reportingYear: number;
  singleFieldOutcomes: SingleFieldOutcomes[];
};

export const OutcomeBasedImpact = ({ isDividePerHectare, maximumPayoutPerHa, reportingYear, singleFieldOutcomes }: OutcomeBasedImpactProps) => {
  const { t } = useTranslation("fieldTimeline");
  const { currentFarm } = useGetFarms();

  const { totalOutcomeBasedPayoutMin, totalOutcomeBasedPayoutMax, totalOutcomePotentialPayout, isMaxOutcomePayoutReached, displayPerHectareOrTotalFarm } =
    useInventoryImpact(reportingYear, singleFieldOutcomes, currentFarm?.id ?? "", currentFarm?.carbonProgram ?? undefined);

  const displayedTotalOutcomeBasedPayoutMin = displayPerHectareOrTotalFarm(totalOutcomeBasedPayoutMin, isDividePerHectare);
  const displayedTotalOutcomeBasedPayoutMax = displayPerHectareOrTotalFarm(totalOutcomeBasedPayoutMax, isDividePerHectare);
  const displayedTotalOutcomePotentialPayout = displayPerHectareOrTotalFarm(totalOutcomePotentialPayout, isDividePerHectare);

  return (
    <>
      <div className="divider" />
      <Tooltip placement="top" title={t("impactPanel.inventory.payoutSocTitle")}>
        <IconLinkButton
          dataTestId="inventory-impact-panel-outcome-based-payout-info-icon-button"
          theme="black-grey-bold"
          iconType="info"
          size="small"
          text={t("impactPanel.inventory.outcomeBasedPayout")}
        />
      </Tooltip>
      <Flex className="margin-top-50" justify="space-between">
        {isMaxOutcomePayoutReached ? (
          <Tag
            className="maximum-payment-tag"
            dataTestId="maximum-payment-tag"
            colour="yellow"
            text={
              <Flex align="center">
                <Typography variant="paragraph-s" bold>
                  {t("impactPanel.inventory.maximumPaymentBadge")}
                </Typography>
              </Flex>
            }
          />
        ) : (
          <Tooltip placement="top" title={t("impactPanel.inventory.payoutSoc")}>
            <Typography variant="h4" data-testid="total-outcome-based-payout" color="green-400">
              {maximumPayoutPerHa === null
                ? formatCurrencyRange(displayedTotalOutcomeBasedPayoutMin, displayedTotalOutcomeBasedPayoutMax, {
                    decimals: 0,
                    currency: currentFarm?.carbonProgram?.currency,
                  })
                : formatCurrencyRange(displayedTotalOutcomeBasedPayoutMin, displayedTotalOutcomeBasedPayoutMax, {
                    decimals: 0,
                    currency: currentFarm?.carbonProgram?.currency,
                  })}
            </Typography>
          </Tooltip>
        )}
        {maximumPayoutPerHa !== null && (
          <Tooltip placement="top" title={t("impactPanel.inventory.payoutSocPotential")}>
            <Typography
              variant="h4"
              color={isMaxOutcomePayoutReached ? "green-400" : undefined}
              className={isMaxOutcomePayoutReached ? undefined : "maximum-theoretical-payout"}
              data-testid="maximum-potential-outcome-based-payout"
            >
              {formatNumericCurrency(displayedTotalOutcomePotentialPayout, { decimals: 0, currency: currentFarm?.carbonProgram?.currency })}
            </Typography>
          </Tooltip>
        )}
      </Flex>
      <div className="divider" />
    </>
  );
};
